import { useTranslations } from '@aikoa/i18n/react'
import { Link, usePathname } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dimensions, Pressable, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import { type Action } from '~/components/Actions'
import Icon, { type IconName, type MaterialCommunityIconName } from '~/components/Icon'
import { ScrollView } from '~/components/ScrollView'
import { ButtonLabel, FakeButton, LinkButton } from '~/components/ui/button'
import { useUserIsAdmin } from '~/data/user/useUserIsAdmin'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils.ts'
import { LanguageSelector } from './LanguageSelector'
import { Text } from './ui/text'
import { PolicyLinks } from './user/PolicyLinks'
import { UserButton } from './UserButton'

interface AppMenuProps {
  isOpen: boolean
  onClose: () => void
  className?: string
  contentClassName?: string
}

const springConfig = {
  damping: 33,
  stiffness: 450,
  mass: 1,
}

export function AppMenu({ isOpen, onClose, className, contentClassName }: AppMenuProps) {
  const { t } = useTranslations('common')
  const [height, setHeight] = useState(Dimensions.get('window').height)

  const isAdmin = useUserIsAdmin()

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', (event) => {
      setHeight(event.window.height)
    })

    return () => subscription.remove()
  }, [])

  // Shared values for animations
  const scaleY = useSharedValue(0)
  const opacity = useSharedValue(0)

  const closeMenu = useCallback(() => {
    // eslint-disable-next-line i18next/no-literal-string
    'worklet'
    scaleY.set(withSpring(0, springConfig))
    opacity.set(withTiming(0, { duration: 200 }))
  }, [opacity, scaleY])

  const openMenu = useCallback(() => {
    // eslint-disable-next-line i18next/no-literal-string
    'worklet'
    scaleY.set(withSpring(1, springConfig))
    opacity.set(withTiming(1, { duration: 200 }))
  }, [opacity, scaleY])

  useEffect(() => {
    if (isOpen) {
      openMenu()
    } else {
      closeMenu()
    }
  }, [isOpen, openMenu, closeMenu])

  const pathname = usePathname()
  const currentPathname = useRef(pathname)

  useEffect(() => {
    if (pathname !== currentPathname.current) {
      currentPathname.current = pathname
      onClose()
    }
  }, [pathname, onClose])

  const menuItems = useMemo(() => {
    return [
      {
        key: 'home',
        label: t(($) => $.menu.home.label),
        icon: 'home',
        href: '/(authenticated)',
        description: t(($) => $.menu.home.description),
      },
      {
        key: 'properties',
        label: t(($) => $.menu.properties.label),
        icon: 'home-group',
        href: '/(authenticated)/properties',
        description: t(($) => $.menu.properties.description),
      },
      {
        key: 'contacts',
        label: t(($) => $.menu.contacts.label),
        icon: 'account-multiple-outline',
        href: '/(authenticated)/contacts',
        description: t(($) => $.menu.contacts.description),
      },
      ...(isAdmin
        ? [
            {
              key: 'admin',
              // eslint-disable-next-line i18next/no-literal-string
              label: 'Admin',
              icon: 'cog' as IconName,
              href: '/(authenticated)/admin',
            },
          ]
        : []),
    ] satisfies Action[]
  }, [t, isAdmin])

  // Animated styles
  const sidebarAnimatedStyle = useAnimatedStyle(() => ({
    height: (height - 87) * scaleY.value,
    // eslint-disable-next-line i18next/no-literal-string
    transformOrigin: 'top',
  }))

  const contentAnimatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scaleY: scaleY.value }],
    // eslint-disable-next-line i18next/no-literal-string
    transformOrigin: 'top',
  }))

  return (
    <Animated.View
      className={cn('relative', isOpen ? 'pointer-events-auto' : 'pointer-events-none', className)}
      style={[sidebarAnimatedStyle]}>
      <ScrollView
        offsetTrackHeight
        indicatorClassName="bg-primary"
        className={cn('h-full flex-1', contentClassName)}
        contentContainerClassName={cn('min-h-full gap-8')}
        style={contentAnimatedStyle}>
        <View className="flex-1 flex-col items-center justify-start">
          {menuItems.map((item) => (
            <Link
              href={item.href}
              key={item.key}
              onPress={() => onClose()}
              className="flex w-full flex-row items-center justify-start border-b border-tertiary-foreground/50 py-2 active:bg-primary">
              <View className="w-full flex-row items-center gap-6 p-4 px-4 pl-5">
                <Icon
                  name={item.icon as MaterialCommunityIconName}
                  size={30}
                  library="materialcommunityicons"
                  className="text-tertiary-foreground"
                  color={designTokens.tertiaryForeground}
                />
                <Text className="flex-1 font-light text-[21px] text-tertiary-foreground">
                  {item.label}
                </Text>
              </View>
            </Link>
          ))}
        </View>
        <View className="mb-6 mt-auto flex-none items-center gap-6">
          {pathname !== '/profile' ? (
            <>
              <Link href="/(authenticated)/profile" className="group gap-6">
                <UserButton showName className="flex-col items-center justify-center gap-3 p-0" />
              </Link>
              <LinkButton variant="default" size="lg" href="/(authenticated)/profile">
                <ButtonLabel>{t(($) => $.menu.realtorProfile)}</ButtonLabel>
              </LinkButton>
            </>
          ) : (
            <Pressable onPress={() => onClose()} className="group gap-6">
              <UserButton showName className="flex-col items-center justify-center gap-3 p-0" />
              <FakeButton variant="default" size="lg">
                <ButtonLabel>{t(($) => $.menu.realtorProfile)}</ButtonLabel>
              </FakeButton>
            </Pressable>
          )}
          <LanguageSelector triggerClassName="mx-6" />
          <PolicyLinks theme="light" />
        </View>
      </ScrollView>
    </Animated.View>
  )
}
